import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme, ThemeButton } from './theme';
import { usePolling } from './poll';

// TM RUSH Prospecting Desk: internal, prospecting only. Data comes live from the n8n outbox.
// Access: open /desk#k=<key> once; the key stays in this browser.
const API = 'https://n8n-hag0.srv1897462.hstgr.cloud/webhook/tmr-desk';
const KEY_STORE = 'tmr_desk_key';

const STATUS = {
  pending_batch: { label: 'Na fila', cls: 'bg-amber-100 text-amber-800' },
  sending: { label: 'Enviando', cls: 'bg-sky-100 text-sky-800' },
  sent: { label: 'Enviado', cls: 'bg-primary/15 text-primary-dark' },
  replied: { label: 'Respondeu', cls: 'bg-emerald-600 text-white' },
  send_failed: { label: 'Falhou', cls: 'bg-red-100 text-red-700' },
  bounced: { label: 'Voltou', cls: 'bg-red-100 text-red-700' },
  opted_out: { label: 'Pediu para sair', cls: 'bg-gray-200 text-gray-700' },
  discarded: { label: 'Descartado', cls: 'bg-gray-200 text-gray-700' },
};

const readKey = () => {
  try {
    const m = window.location.hash.match(/k=([A-Za-z0-9_-]+)/);
    if (m) {
      try { localStorage.setItem(KEY_STORE, m[1]); } catch (e) { /* storage blocked */ }
      window.history.replaceState(null, '', window.location.pathname);
      return m[1];
    }
    return localStorage.getItem(KEY_STORE) || '';
  } catch (e) {
    return '';
  }
};

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
};

const Pill = ({ status }) => {
  const s = STATUS[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-700' };
  return <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${s.cls}`}>{s.label}</span>;
};

const Stat = ({ label, value, hint }) => (
  <div className="bg-background-white rounded-2xl p-4 md:p-5 border border-gray-100/80 shadow-sm">
    <div className="text-[11px] md:text-xs font-semibold uppercase tracking-wider text-text/50 mb-1">{label}</div>
    <div className="text-2xl md:text-3xl font-bold text-text leading-tight">{value}</div>
    {hint && <div className="text-xs text-text/50 mt-1">{hint}</div>}
  </div>
);

const DeskPage = () => {
  const [key] = useState(readKey);
  const [data, setData] = useState(null);
  const [theme, toggleTheme] = useTheme('light', '#fcfcf9');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState('all');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Prospecting Desk | TM RUSH';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.title = prevTitle;
      document.head.removeChild(meta);
    };
  }, []);

  const load = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}?k=${encodeURIComponent(key)}`);
      const j = await r.json();
      if (!j || j.ok === false) throw new Error((j && j.error) || 'Sem acesso');
      setData(j);
    } catch (e) {
      setError(e.message || 'Não consegui carregar');
    } finally {
      setLoading(false);
    }
  }, [key]);

  // atualiza a cada minuto só com a aba na tela (aba escondida não consulta o servidor)
  usePolling(load, 60000);

  const rows = useMemo(() => [...((data && data.rows) || [])].sort((a, b) => {
    const ta = a.replied_at || a.sent_at || '';
    const tb = b.replied_at || b.sent_at || '';
    if (!!ta !== !!tb) return ta ? -1 : 1;
    return tb.localeCompare(ta) || String(a.to_name || '').localeCompare(String(b.to_name || ''));
  }), [data]);
  const batches = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.batch, r.batch_label || r.batch));
    return [...m.entries()];
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => (batch === 'all' || r.batch === batch)
      && (status === 'all' || r.status === status)
      && (!needle || [r.to_name, r.to_email, r.firm, r.clients, r.marks, r.subject].join(' ').toLowerCase().includes(needle)));
  }, [rows, batch, status, q]);

  const count = (st) => rows.filter((r) => r.status === st).length;
  const sent = rows.filter((r) => ['sent', 'replied', 'opted_out', 'bounced'].includes(r.status)).length;
  const replied = count('replied');

  if (!key) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="bg-background-white rounded-2xl p-8 max-w-md w-full text-center shadow-sm">
          <img src="/images/tm-rushlogo.svg" alt="TM RUSH" className="h-7 mx-auto mb-5" />
          <h1 className="text-xl font-bold text-text mb-2">Prospecting Desk</h1>
          <p className="text-text/70 text-sm">Abra pelo link de acesso que você recebeu.</p>
          <a href="/entrar?ir=/desk" className="inline-block mt-5 text-sm font-semibold text-primary-dark hover:underline">Entrar com e-mail e senha</a>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <header className="tmr-keep bg-text text-white">
        <div className="container mx-auto px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/images/tm-rushlogo.svg" alt="TM RUSH" className="tmr-logo h-6 brightness-0 invert" />
            <span className="text-sm font-semibold bg-white/10 border border-white/20 rounded-full px-3 py-1">Prospecting Desk</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-300">
            <span>{data ? `Atualizado ${fmtDate(data.generated_at)}` : loading ? 'Carregando…' : ''}</span>
            <ThemeButton theme={theme} onToggle={toggleTheme} className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 font-semibold text-white" />
            <button type="button" onClick={load} className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 font-semibold text-white">
              Atualizar
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <p className="text-text/60 text-sm mb-6">Só prospecção. Os e-mails do Brazil Watch para clientes ficam fora daqui.</p>
        {error && <div className="bg-red-50 text-red-700 rounded-xl p-4 mb-6 text-sm">{error}</div>}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-8">
          <Stat label="Enviados" value={sent} />
          <Stat label="Responderam" value={replied} hint={sent ? `${Math.round((replied / sent) * 100)}% dos enviados` : ''} />
          <Stat label="Na fila" value={count('pending_batch')} />
          <Stat label="Falhas" value={count('send_failed') + count('bounced')} />
          <Stat label="Pediram para sair" value={count('opted_out')} />
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por escritório, cliente, marca ou e-mail"
            className="w-full md:max-w-sm rounded-xl border border-gray-200 bg-background-white px-4 py-2.5 text-sm"
          />
          <select value={batch} onChange={(e) => setBatch(e.target.value)} className="rounded-xl border border-gray-200 bg-background-white px-3 py-2.5 text-sm">
            <option value="all">Todos os lotes</option>
            {batches.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-gray-200 bg-background-white px-3 py-2.5 text-sm">
            <option value="all">Todos os status</option>
            {Object.entries(STATUS).map(([id, s]) => <option key={id} value={id}>{s.label}</option>)}
          </select>
          <span className="text-sm text-text/50 md:ml-auto">{filtered.length} de {rows.length}</span>
        </div>

        <div className="space-y-3">
          {filtered.map((r) => (
            <div key={r.msg_id} className="bg-background-white rounded-2xl border border-gray-100/80 shadow-sm">
              <button type="button" onClick={() => setOpen(open === r.msg_id ? null : r.msg_id)} className="w-full text-left p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                <div className="min-w-0 md:w-[34%]">
                  <div className="font-semibold text-text truncate">{r.to_name || r.to_email}</div>
                  <div className="text-xs text-text/60 truncate">{r.firm ? `${r.firm} · ` : ''}{r.to_email}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-text truncate">{r.clients}</div>
                  <div className="text-xs text-text/60 truncate">{r.marks}</div>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 md:flex-nowrap md:shrink-0 md:justify-end">
                  <span className="text-xs text-text/50 whitespace-nowrap">{r.batch_label || r.batch}</span>
                  <span className="text-xs text-text/50 whitespace-nowrap">{fmtDate(r.replied_at || r.sent_at)}</span>
                  <Pill status={r.status} />
                </div>
              </button>
              {open === r.msg_id && (
                <div className="border-t border-gray-100 p-4 md:p-5">
                  <div className="text-sm font-semibold text-text mb-3">{r.subject}</div>
                  {r.reply_summary && (
                    <div className="bg-emerald-50 text-emerald-900 text-sm rounded-xl p-3 mb-3">
                      <span className="font-semibold">Resposta: </span>{r.reply_summary}
                    </div>
                  )}
                  <iframe title={`E-mail para ${r.to_email}`} sandbox="" srcDoc={r.body_html} className="w-full h-[520px] rounded-xl border border-gray-100 bg-white" />
                </div>
              )}
            </div>
          ))}
          {!filtered.length && !loading && <div className="text-center text-text/50 py-16">Nada por aqui com esses filtros.</div>}
        </div>
      </div>
    </main>
  );
};

export default DeskPage;
